import {
  createToastTimerState,
  reduceToastTimer,
  type ToastTimerEvent,
  timerDelayMs,
} from "@lifter/hub-ui/browser-shared";
import { AlertTriangle, CheckCircle2, X, XCircle } from "lucide-react";
import type { ComponentType, ReactNode, SVGProps } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "./button";
import { LiveRegion } from "./live-region";

export type ToastTone = "success" | "warning" | "danger";

interface ToastInputBase {
  action?: {
    label: string;
    onClick(): void;
  };
  message: string;
}

export type ToastInput =
  | (ToastInputBase & { tone: "danger"; timeoutMs?: never })
  | (ToastInputBase & { tone: Exclude<ToastTone, "danger">; timeoutMs?: number });

type ToastRecord = ToastInput & { id: number };

interface ToastContextValue {
  show(toast: ToastInput): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((toast: ToastInput) => {
    const id = nextId.current;
    nextId.current += 1;
    setToasts((current) => enqueueToast(current, { ...toast, id }));
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport dismiss={dismiss} toasts={toasts} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("ToastProvider is missing");
  }
  return context;
}

function ToastViewport({ dismiss, toasts }: { dismiss(id: number): void; toasts: ToastRecord[] }) {
  return (
    // Overlays portal to document.body, which puts them after #root in DOM
    // order — at an equal z-index they would paint over this viewport. Toasts
    // are feedback about actions taken inside those overlays, so they must
    // outrank the tallest overlay layer (the z-[100] mobile drawer).
    //
    // A stacked feed announces only the added card. LiveRegion owns the live
    // role; cards stay plain so each toast is announced once.
    <LiveRegion
      atomic={false}
      className="pointer-events-none fixed right-[var(--toast-offset-x)] top-[var(--toast-offset-y)] z-[110] grid w-[min(420px,calc(100vw-32px))] gap-3"
    >
      {toasts.map((toast) => (
        <ToastCard dismiss={() => dismiss(toast.id)} key={toast.id} toast={toast} />
      ))}
    </LiveRegion>
  );
}

const TONE_STYLES: Record<
  ToastTone,
  {
    accent: string;
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    iconClass: string;
  }
> = {
  success: {
    accent: "before:bg-[var(--color-success)]",
    icon: CheckCircle2,
    iconClass: "text-[var(--color-success)]",
  },
  warning: {
    accent: "before:bg-[var(--color-warning)]",
    icon: AlertTriangle,
    iconClass: "text-[var(--color-warning)]",
  },
  danger: {
    accent: "before:bg-[var(--color-danger)]",
    icon: XCircle,
    iconClass: "text-[var(--color-danger)]",
  },
};

function ToastCard({ dismiss, toast }: { dismiss(): void; toast: ToastRecord }) {
  const tone = TONE_STYLES[toast.tone];
  const Icon = tone.icon;
  const timeoutMs = effectiveToastTimeoutMs(toast);

  // Pause while focused or hovered; resume with the *remaining* budget so a
  // toast the user keeps brushing past still finishes its original lifetime.
  const [timer, setTimer] = useState(() => createToastTimerState(timeoutMs, monotonicNow()));
  const apply = useCallback(
    (event: ToastTimerEvent) => setTimer((state) => reduceToastTimer(state, event, monotonicNow())),
    [],
  );

  useEffect(() => {
    const delay = timerDelayMs(timer, monotonicNow());
    if (delay === null) return undefined;
    const timeout = globalThis.setTimeout(dismiss, delay);
    return () => globalThis.clearTimeout(timeout);
  }, [dismiss, timer]);

  const persistent = timeoutMs <= 0;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Hover/focus only pause auto-dismiss; the toast's actions remain explicit buttons.
    <div
      aria-atomic="true"
      className={`pointer-events-auto relative flex items-start gap-3 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-3 pl-4 pr-3 shadow-[0_10px_25px_-12px_rgba(26,26,26,0.18),0_2px_6px_-2px_rgba(26,26,26,0.08)] before:absolute before:inset-y-0 before:left-0 before:w-1 ${tone.accent} motion-safe:animate-[toast-slide-in_180ms_ease-out]`}
      onBlur={() => apply({ type: "blur" })}
      onFocus={() => apply({ type: "focus" })}
      onMouseEnter={() => apply({ type: "mouseenter" })}
      onMouseLeave={() => apply({ type: "mouseleave" })}
    >
      <Icon aria-hidden="true" className={`mt-0.5 size-4.5 shrink-0 ${tone.iconClass}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-5 text-[var(--color-text)]">{toast.message}</p>
        {persistent ? (
          <p className="type-meta mt-1 text-[var(--color-text-tertiary)]">Close when ready.</p>
        ) : null}
      </div>
      {toast.action ? (
        <Button
          onClick={() => {
            toast.action?.onClick();
            dismiss();
          }}
          size="sm"
          variant="secondary"
        >
          {toast.action.label}
        </Button>
      ) : null}
      <Button
        aria-label="Dismiss notification"
        iconOnly
        onClick={dismiss}
        size="sm"
        variant="ghost"
      >
        <X aria-hidden="true" className="size-4" />
      </Button>
    </div>
  );
}

function defaultToastTimeoutMs(tone: ToastTone): number {
  if (tone === "success") return 4000;
  if (tone === "warning") return 6000;
  return 0;
}

function enqueueToast(current: ToastRecord[], incoming: ToastRecord): ToastRecord[] {
  if (
    current.some(
      (toast) =>
        toast.tone === incoming.tone &&
        toast.message === incoming.message &&
        toast.action?.label === incoming.action?.label &&
        effectiveToastTimeoutMs(toast) === effectiveToastTimeoutMs(incoming),
    )
  ) {
    return current;
  }

  if (current.length < 4) return [...current, incoming];

  const oldestTransient = current.findIndex((toast) => !isPersistentToast(toast));
  if (oldestTransient < 0) {
    return isPersistentToast(incoming) ? [...current, incoming] : current;
  }

  return [...current.slice(0, oldestTransient), ...current.slice(oldestTransient + 1), incoming];
}

function isPersistentToast(toast: ToastInput): boolean {
  return effectiveToastTimeoutMs(toast) <= 0;
}

function effectiveToastTimeoutMs(toast: ToastInput): number {
  return toast.timeoutMs ?? defaultToastTimeoutMs(toast.tone);
}

// Monotonic clock for elapsed-time accounting: `performance.now()` can't be
// dragged backwards by a wall-clock adjustment mid-toast the way `Date.now()`
// can. Falls back to the wall clock only where `performance` is absent.
function monotonicNow(): number {
  return globalThis.performance?.now() ?? Date.now();
}
